import { useCallback } from 'react';

import { PaymentFrequency, PolicyHolder, CoverType } from 'scenes/common/types';
import { makeApiLoaded, NOT_YET_LOADED } from 'scenes/common/helpers/apiData';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';

import { KickOutChoice } from 'scenes/home/flows/quoteDetails/types';
import {
  apiResponse,
  setAdditionalPolicyHolders,
  setCoverType,
  setKickOutChoice,
  setPreselectedPaymentFrequency,
} from 'scenes/home/actions';
import {
  HomeFunnelState,
  HomeFormAnswer,
  HomeApiResponses,
  ReferenceData,
} from 'scenes/home/types';
import { useConfigActions } from 'scenes/common/hooks/useActions/useConfigActions';
import { isMonthlyPaymentPreselected } from 'scenes/home/selectors';

import useFlowActions from './useFlowActions';
import { useQuoteActions } from './useQuoteActions';
import { useCoverOptionActions } from './useCoverOptionActions';
import { usePaymentActions } from './usePaymentActions';
import usePolicyActions from './usePolicyActions';
import { useCampaignActions } from './useCampaignActions';

const useActions = (
  handleAnswer: (f: HomeFormAnswer) => void,
  referenceData: ReferenceData
) => {
  const funnelState = useFunnelState<HomeFunnelState>();
  const flowActions = useFlowActions(handleAnswer);
  const {
    onNextIfLocationIs,
    onNext,
    onPrevious,
    onRestartQuote,
    onNextWithReset,
  } = useFlowActions(handleAnswer);
  const onApiResponse = useCallback(
    (response: Partial<HomeApiResponses>) => {
      handleAnswer(apiResponse(response));
    },
    [handleAnswer]
  );

  const onClearApiErrors = useCallback(() => {
    onApiResponse({
      quoteDetails: funnelState.apiResponses.quoteDetails.data
        ? makeApiLoaded(funnelState.apiResponses.quoteDetails)
        : NOT_YET_LOADED,
    });
  }, [onApiResponse, funnelState.apiResponses]);

  const onSetCoverType = useCallback(
    (coverType: CoverType) => {
      handleAnswer(setCoverType(coverType));
    },
    [handleAnswer]
  );

  const onAddAdditionalPolicyHolder = useCallback(
    (newPolicyHolder: PolicyHolder) => {
      handleAnswer(
        setAdditionalPolicyHolders([
          ...funnelState.policyDetails.additionalPolicyHolders,
          newPolicyHolder,
        ])
      );
      onNextIfLocationIs(funnelState.step);
    },
    [
      funnelState.policyDetails.additionalPolicyHolders,
      funnelState.step,
      handleAnswer,
      onNextIfLocationIs,
    ]
  );

  const onUpdateAdditionalPolicyHolder = useCallback(
    (policyHolderIndex: number, policyHolder: PolicyHolder) => {
      const copyPolicyHolders = [
        ...funnelState.policyDetails.additionalPolicyHolders,
      ];
      copyPolicyHolders[policyHolderIndex] = policyHolder;

      handleAnswer(setAdditionalPolicyHolders(copyPolicyHolders));
      onNextIfLocationIs(funnelState.step);
    },
    [
      funnelState.policyDetails.additionalPolicyHolders,
      funnelState.step,
      handleAnswer,
      onNextIfLocationIs,
    ]
  );

  const onRemoveAdditionalPolicyHolder = useCallback(
    (policyHolderIndex: number) => {
      handleAnswer(
        setAdditionalPolicyHolders(
          funnelState.policyDetails.additionalPolicyHolders.filter(
            (_, i) => i !== policyHolderIndex
          )
        )
      );
    },
    [funnelState.policyDetails.additionalPolicyHolders, handleAnswer]
  );

  const quoteActions = useQuoteActions(
    handleAnswer,
    onNextIfLocationIs,
    onApiResponse,
    referenceData
  );

  const coverOptionActions = useCoverOptionActions(
    handleAnswer,
    onNextIfLocationIs,
    onApiResponse,
    referenceData
  );

  const policyActions = usePolicyActions(
    onNextIfLocationIs,
    onApiResponse,
    referenceData
  );

  const paymentActions = usePaymentActions(
    handleAnswer,
    onNextIfLocationIs,
    onApiResponse,
    referenceData
  );

  const campaignActions = useCampaignActions(
    funnelState,
    onApiResponse,
    handleAnswer
  );

  const configActions = useConfigActions(onApiResponse);

  const onUpdateKickOutChoice = useCallback(
    (choice: string) => {
      handleAnswer(setKickOutChoice(choice as KickOutChoice));
    },
    [handleAnswer]
  );

  const onSwitchPaymentFrequency = useCallback(() => {
    handleAnswer(
      setPreselectedPaymentFrequency(
        isMonthlyPaymentPreselected(funnelState)
          ? PaymentFrequency.Annually
          : PaymentFrequency.Monthly
      )
    );
  }, [funnelState, handleAnswer]);

  return {
    onNext,
    onNextWithReset,
    onPrevious,
    onRestartQuote,
    onClearApiErrors,
    onSetCoverType,
    onAddAdditionalPolicyHolder,
    onUpdateAdditionalPolicyHolder,
    onRemoveAdditionalPolicyHolder,
    onEditQuoteDetails: flowActions.onEditQuoteDetails,
    onEditPolicyDetails: flowActions.onEditPolicyDetails,
    onEditCoverDetails: flowActions.onEditCoverDetails,
    ...quoteActions,
    ...coverOptionActions,
    ...policyActions,
    ...paymentActions,
    ...campaignActions,
    onUpdateKickOutChoice,
    onSwitchPaymentFrequency,
    ...configActions,
  };
};

export default useActions;
